import type { Release } from "@packages/commons";

import { useCurrentRelease } from "@/common/hooks";
import { getManagedResourceOwner, getWorkflowStep } from "@/features/adapters/model";
import type { ManagedResourceKind } from "@/features/adapters/model/runtime/ownership";
import { ManagedResourceBadge } from "@/features/adapters/components/ManagedResourceBadge";
import { useOptionalAdapterWorkflow } from "@/features/adapters/context/AdapterWorkflowContext";
import { getAdapterWorkflowStepState, getReleaseAdapterWorkflow } from "@/features/adapters/persistence";

interface AdapterResourceOwnershipProps {
  kind: ManagedResourceKind;
  resourceId?: string;
  release?: Release;
  onNavigate?: () => void;
  className?: string;
}

export const AdapterResourceOwnership = ({ kind, resourceId, release, onNavigate, className }: AdapterResourceOwnershipProps) => {
  const currentRelease = useCurrentRelease();
  const activeRelease = release ?? currentRelease;
  const workflow = useOptionalAdapterWorkflow();
  const owner = activeRelease ? getManagedResourceOwner(activeRelease, kind, resourceId) : undefined;
  if (!owner) return null;

  const step = getWorkflowStep(owner.definition, owner.stepId);
  const currentStep = Boolean(workflow?.isOpen && workflow.definitionId === owner.definition.id && workflow.stepKey === owner.stepId);
  const status = getAdapterWorkflowStepState(
    workflow?.definitionId === owner.definition.id
      ? workflow.stepStatus
      : getReleaseAdapterWorkflow(activeRelease, owner.definition.id)?.stepStatus,
    owner.stepId,
  ) ?? "not-started";

  return <div className={className}>
    <ManagedResourceBadge
      currentStep={currentStep}
      disabled={!workflow}
      status={status}
      stepName={step?.label ?? "Adapter resource"}
      onToggleGuide={() => {
        if (!workflow) return;
        if (currentStep) {
          workflow.closeAdapter();
          return;
        }
        if (workflow.openAdapterAtStep(owner.definition.id, owner.stepId)) onNavigate?.();
      }}
    />
  </div>;
};
